/** Viewport facts the layouts branch on. Connectivity is `connection.svelte.ts`. */

/**
 * The app's sections (Phase 11 plan §1.2). On a phone each is a nav item and
 * fills the screen; on desktop the map is always there, so `map` is not a
 * section and reads as `forecast`.
 */
export const VIEWS = ['forecast', 'map', 'models', 'places', 'settings'] as const
export type View = (typeof VIEWS)[number]

const VIEW_PARAM = 'view'

/** Below this the sidebar cannot hold a forecast row without wrapping. */
const DESKTOP_QUERY = '(min-width: 900px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function matches(query: string): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.(query).matches
}

/**
 * A `?view=` value as a section: anything unknown is `forecast`, and so is
 * `map` on desktop, where the map is beside every section rather than one.
 */
export function parseView(raw: string | null, isDesktop: boolean): View {
  const view = (VIEWS as readonly string[]).includes(raw ?? '') ? (raw as View) : 'forecast'
  return view === 'map' && isDesktop ? 'forecast' : view
}

function viewFromUrl(isDesktop: boolean): View {
  if (typeof window === 'undefined') return 'forecast'
  return parseView(new URLSearchParams(window.location.search).get(VIEW_PARAM), isDesktop)
}

export const layout = $state({
  isDesktop: matches(DESKTOP_QUERY),
  /**
   * The section on screen (Phase 11 plan §4.3). Named in the address bar as
   * `?view=` so a screenshot, a test and a link can name one, and replaced
   * there rather than pushed: back does not walk through tabs (§2.2).
   * Both layouts render by it: the phone's nav (R.2) and the desktop's rail
   * (R.4).
   */
  view: viewFromUrl(matches(DESKTOP_QUERY)),
  reducedMotion: matches(REDUCED_MOTION_QUERY),
})

/**
 * Settings is a section (plan §2.6): the phone's fifth nav item, and the
 * rail's last button on the desktop.
 */
export function openSettings(): void {
  setView('settings')
}

/**
 * Show a section, and name it in the address bar with `history.replaceState`:
 * `forecast` is the bare URL, anything else `?view=`. Every other parameter
 * and the hash are left as they are. `map` on desktop is `forecast`.
 */
export function setView(view: View): void {
  layout.view = parseView(view, layout.isDesktop)
  writeView(layout.view)
}

function writeView(view: View): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (view === 'forecast') url.searchParams.delete(VIEW_PARAM)
  else url.searchParams.set(VIEW_PARAM, view)
  const next = `${url.pathname}${url.search}${url.hash}`
  if (next === `${window.location.pathname}${window.location.search}${window.location.hash}`) return
  try {
    history.replaceState(history.state, '', next)
  } catch {
    // A sandboxed frame can refuse it; the section still changes.
  }
}

/** Wire the listeners. Returns a teardown so tests and HMR do not stack them. */
export function watchLayout(): () => void {
  if (typeof window === 'undefined') return () => {}

  // The address bar at launch, read again here because the module may have
  // been loaded before it was the one this page opened with.
  layout.view = viewFromUrl(layout.isDesktop)
  writeView(layout.view)

  const desktop = window.matchMedia(DESKTOP_QUERY)
  const motion = window.matchMedia(REDUCED_MOTION_QUERY)
  const onDesktop = (e: MediaQueryListEvent) => {
    layout.isDesktop = e.matches
    // A phone on Map widened past the breakpoint: the map is beside the
    // panel now, and the panel shows the forecast.
    if (e.matches && layout.view === 'map') setView('forecast')
  }
  const onMotion = (e: MediaQueryListEvent) => (layout.reducedMotion = e.matches)

  desktop.addEventListener('change', onDesktop)
  motion.addEventListener('change', onMotion)

  return () => {
    desktop.removeEventListener('change', onDesktop)
    motion.removeEventListener('change', onMotion)
  }
}

